import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  signal, computed, ChangeDetectionStrategy, HostListener, ElementRef, ViewChild, effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FileDataModel, ClientAction } from 'src/app/models/file-upload/file-data';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslationsProviderService } from 'src/app/services/translations-provider/translations-provider.service';
import { RestProviderService } from 'src/app/services/rest-provider/rest-provider.service';
import { UserService } from 'src/app/services/user/user-provider.service';
import { RequestModel } from 'src/app/models/request/request-model';
import { firstValueFrom } from 'rxjs';
import { Annotorious } from '@recogito/annotorious';
import { TiffPreviewService } from 'src/app/services/tiff-preview/tiff-preview.service';

// ── File helper types ──

/**
 * What the centre panel can show for one file.
 *
 * 'none' has never been asked for; a file is only fetched full size when it is selected.
 * 'too-large' and 'unsupported' are the two ways a TIFF can fail to become a picture, and they
 * are separate states because the panel has to SAY which one happened. Neither of them is a
 * blank frame and neither of them is a broken <img>. A PDF, text, audio or video file whose
 * bytes could not be fetched is 'unsupported' too, and the panel offers the download instead.
 */
type PreviewState = 'none' | 'loading' | 'ready' | 'too-large' | 'unsupported';

type FileType = 'image' | 'pdf' | 'document' | 'text' | 'audio' | 'video' | 'other';

interface MediaFile {
  data: FileDataModel;
  /** The server's PNG thumbnail. Empty means there is none, and the tile says so in words. */
  thumbUrl: SafeUrl | string;
  /** A trusted URL for <img>/<audio>/<video>, or a trusted RESOURCE URL for the PDF <iframe>. */
  previewUrl: SafeUrl | SafeResourceUrl | string;
  previewState: PreviewState;
  /** The decoded body of a text file, shown as-is. Only set for fileType 'text'. */
  textContent: string;
  fileType: FileType;
  extension: string;
  friendlySize: string;
}

// GET /api/media/properties/{fileId}. Three sources, none of them a new column: the row in
// dbo.filedata, the entries in dbo.auditlog keyed to this FileID, and what the bytes assert.
interface FileMetadataItem { Key: string; Value: string; }
interface FileMetadataGroup { Key: string; Items: FileMetadataItem[]; }

interface FileHistoryEntry {
  AuditLogId: number;
  Date: string;
  EntryType: string;
  Username: string | null;
  Detail: string | null;
  Success: boolean | null;
  SourceChecksum: string | null;
  TargetChecksum: string | null;
}

interface FileSystemFacts {
  FileId: number;
  Name: string;
  MimeType: string | null;
  Extension: string | null;
  SizeBytes: number;
  UploadDate: string;
  LastModificationDate: string;
  Checksum: string | null;
  SystemSignature: string | null;
  BackendStore: string | null;
  StoragePath: string | null;
  Category: string | null;
  FileCategory: string | null;
  FileOrder: number;
  UniquePermissions: boolean;
  DocumentId: number;
}

interface FileProperties {
  Available: boolean;
  System: FileSystemFacts | null;
  History: FileHistoryEntry[] | null;
  Embedded: FileMetadataGroup[] | null;
  EmbeddedReadNote: string | null;
}

type ViewMode = 'grid' | 'list';
type InfoTab = 'details' | 'ai' | 'annotations' | 'properties';
type ZoomMode = 'fit' | 'fill' | 'actual';

// ── Image extensions ──
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'];

/** The extensions no browser but Safari decodes, so the tab has to decode them itself. */
const TIFF_EXTS = ['tif', 'tiff'];

/**
 * The tile size asked of the server. GetPreview resizes with Magick and writes PNG for every
 * image MIME it is handed, TIFF included, so the sidebar gets a picture the browser can draw
 * whatever the original was - and gets 30 KB of it instead of the 60 MB original.
 */
const THUMB_EDGE = 256;
const PDF_EXTS = ['pdf'];
const DOC_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
const TEXT_EXTS = ['txt', 'csv', 'json', 'xml', 'html', 'md', 'log'];
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'flac', 'aac'];
const VIDEO_EXTS = ['mp4', 'webm', 'avi', 'mov', 'mkv'];

/**
 * The types the centre panel draws from the file's own bytes: images in an <img>, PDFs in the
 * browser's own viewer, text as text, audio and video in their media elements. Everything else
 * (Office documents, archives, unknown) has no in-browser renderer and is offered for download.
 */
const INLINE_TYPES: FileType[] = ['image', 'pdf', 'text', 'audio', 'video'];

/**
 * The MIME the blob is handed to the browser under. dbo.filedata's MimeType column is empty
 * for most legacy rows, and a PDF blob typed application/octet-stream is a download prompt in
 * the iframe rather than a rendered document - so the extension decides when the row cannot.
 */
const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain', csv: 'text/csv', json: 'application/json', xml: 'application/xml',
  html: 'text/html', md: 'text/markdown', log: 'text/plain',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', flac: 'audio/flac', aac: 'audio/aac',
  mp4: 'video/mp4', webm: 'video/webm', avi: 'video/x-msvideo', mov: 'video/quicktime', mkv: 'video/x-matroska',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', bmp: 'image/bmp',
  webp: 'image/webp', svg: 'image/svg+xml', tif: 'image/tiff', tiff: 'image/tiff',
};

@Component({
  selector: 'app-media-explorer-v2',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './media-explorer-v2.component.html',
})
export class MediaExplorerV2Component implements OnInit, OnDestroy {
  // ── Inputs (same interface as old component) ──
  @Input() files: FileDataModel[] = [];
  @Input() shownFiles: any[] = [];
  @Input() type: number = 1; // 1=images, 2=files
  @Input() readOnlyFields: boolean = false;
  @Input() model: any;
  @Input() dialogRef: MatDialogRef<any> | null = null;
  @Input() hasDeleteRight: boolean = false;
  @Input() hasWriteRight: boolean = false;
  @Input() availableExtensions: any[] = [];

  // ── Outputs ──
  @Output() onItemDownload = new EventEmitter<FileDataModel>();
  @Output() onItemDelete = new EventEmitter<FileDataModel>();
  @Output() onItemAdd = new EventEmitter<void>();
  @Output() filesChanged = new EventEmitter<FileDataModel[]>();

  // ── ViewChild ──
  @ViewChild('imageEl') imageEl!: ElementRef<HTMLImageElement>;
  @ViewChild('filmstripEl') filmstripEl!: ElementRef<HTMLDivElement>;

  // ── Annotorious ──
  private imageAnnotate: any = null;
  annotations = signal<any[]>([]);

  // ── State (signals) ──
  mediaFiles = signal<MediaFile[]>([]);
  selectedIndex = signal<number>(0);
  viewMode = signal<ViewMode>('grid');
  activeTab = signal<InfoTab>('details');
  zoomMode = signal<ZoomMode>('fit');
  zoomLevel = signal<number>(100);
  isFullscreen = signal<boolean>(false);
  annotationMode = signal<boolean>(false);
  annotationsVisible = signal<boolean>(true);
  searchText = signal<string>('');
  downloadInProgress = signal<boolean>(false);
  rotation = signal<number>(0);
  flippedH = signal<boolean>(false);

  // -- Format conversion (TIFF export), inside the Lastnosti tab --
  exportInProgress = signal<boolean>(false);
  exportError = signal<string | null>(null);

  // -- File properties (Lastnosti) --
  properties = signal<FileProperties | null>(null);
  propertiesLoading = signal<boolean>(false);
  /**
   * The properties call did not answer. The export action is rendered DISABLED with this as its
   * reason rather than hidden: a transient failure that removes the affordance makes the feature
   * look absent from the product, which is a different and worse claim than "not right now".
   */
  propertiesUnavailable = signal<boolean>(false);
  // Read once per file for the life of the dialog. The server reads the bytes to answer, so
  // re-selecting a 60 MB TIFF must not read it again.
  private propertiesCache = new Map<number, FileProperties>();

  // ── Computed ──
  selectedFile = computed(() => {
    const files = this.mediaFiles();
    const idx = this.selectedIndex();
    return idx >= 0 && idx < files.length ? files[idx] : null;
  });

  filteredFiles = computed(() => {
    const search = this.searchText().toLowerCase();
    if (!search) return this.mediaFiles();
    return this.mediaFiles().filter(f => f.data.Name.toLowerCase().includes(search));
  });

  fileCount = computed(() => this.mediaFiles().length);

  /** ModelWrite, the right the server gates the conversion on. Set once in the constructor. */
  mayConvert = false;

  /**
   * Whether this user may convert this file at all. ModelWrite is the right the server gates
   * api/media/convert on, and it is asked here so the affordance is absent for a user who can
   * never perform it. A transient read failure is NOT part of this question - see
   * exportDisabledReason.
   */
  canExport = computed(() => {
    const f = this.selectedFile();
    return this.mayConvert && !!f && f.fileType === 'image' && !!f.data.FileID && f.data.FileID > 0;
  });

  /** Why the export button is disabled right now, or null when it is not. */
  exportDisabledReason = computed<string | null>(() => {
    if (!this.propertiesUnavailable()) { return null; }
    return this.translationsService.translate('FileExplorer_ExportUnavailable');
  });

  imageTransform = computed(() => {
    const z = this.zoomLevel();
    const r = this.rotation();
    const f = this.flippedH();
    const parts: string[] = [];
    if (z !== 100) parts.push(`scale(${z / 100})`);
    if (r !== 0) parts.push(`rotate(${r}deg)`);
    if (f) parts.push('scaleX(-1)');
    return parts.length ? parts.join(' ') : 'none';
  });

  constructor(
    private sanitizer: DomSanitizer,
    public translationsService: TranslationsProviderService,
    private restProvider: RestProviderService,
    private userService: UserService,
    private tiffPreview: TiffPreviewService,
  ) {
    // The server gates api/media/convert on ModelWrite over the owning record
    // (StorageService.ReadForConversion). Ask the same question here so the affordance is not
    // offered to a user who can never perform it. Rights do not change inside a session.
    this.mayConvert = this.userService.hasRight('ModelWrite');

    // Reactively apply zoom/rotate/flip to the Annotorious wrapper (image + SVG)
    effect(() => {
      const transform = this.imageTransform();
      const frame = document.querySelector('.me2-preview-frame');
      if (!frame) return;
      const imgEl = frame.querySelector('#me2-annotate') as HTMLElement;
      if (!imgEl) return;
      const parent = imgEl.parentElement;
      if (parent && parent !== frame) {
        // Annotorious wrapper exists — transform it (contains both img + SVG overlay)
        (parent as HTMLElement).style.transform = transform;
        (parent as HTMLElement).style.transition = 'transform 0.2s ease';
      } else {
        // No wrapper yet — transform image directly
        imgEl.style.transform = transform;
        imgEl.style.transition = 'transform 0.2s ease';
      }
    });
  }

  ngOnInit(): void {
    this.buildMediaFiles();
    this.loadProperties();
    this.loadThumbnails();
    this.ensurePreview(this.selectedIndex());
  }

  ngOnDestroy(): void {
    if (this.imageAnnotate) {
      try { this.imageAnnotate.destroy(); } catch {}
    }
    this.revokeObjectUrls();
  }

  // ── Object URL hygiene ──
  //
  // Every URL.createObjectURL pins its blob until it is revoked, and buildMediaFiles used to
  // run once per file as the previews arrived - so opening a record with twelve images minted
  // and abandoned dozens of full-size originals, none of them revoked, for as long as the tab
  // lived. Each URL is now held against the file it belongs to, so a rebuild can release the
  // ones whose file has gone and keep the ones whose file is still there; the dialog closing
  // releases the rest.
  private objectUrls = new Map<string, string>();

  private trackObjectUrl(p_key: string, p_url: string): string {
    const stale = this.objectUrls.get(p_key);
    if (stale && stale !== p_url) {
      try { URL.revokeObjectURL(stale); } catch { }
    }
    this.objectUrls.set(p_key, p_url);
    return p_url;
  }

  /** Releases every tracked URL whose file is no longer in the list. */
  private revokeObjectUrlsExcept(p_liveKeys: Set<string>): void {
    this.objectUrls.forEach((url, key) => {
      if (p_liveKeys.has(key)) { return; }
      try { URL.revokeObjectURL(url); } catch { }
      this.objectUrls.delete(key);
    });
  }

  private revokeObjectUrls(): void {
    this.revokeObjectUrlsExcept(new Set<string>());
  }

  /** The key a file's object URLs are held under. Stable across rebuilds. */
  private urlKey(p_file: FileDataModel, p_index: number): string {
    return p_file?.FileID > 0 ? 'id:' + p_file.FileID : 'local:' + p_index + ':' + (p_file?.Name ?? '');
  }

  /**
   * Builds the MediaFile array from the input list.
   *
   * Nothing is fetched here. The tile comes from the server's PNG thumbnail (loadThumbnails)
   * and the centre panel from the full-size original of the SELECTED file only
   * (ensurePreview). It used to blob the full original into both, which drew a 64 px tile out
   * of a 60 MB download - and, for a TIFF, drew nothing at all, because no browser but Safari
   * decodes TIFF and the tile was a broken image icon over a raw GUID.
   *
   * A file the user has just added locally has no row on the server yet, so its own bytes are
   * the only thing there is to show; that one keeps an object URL, tracked for revocation.
   */
  buildMediaFiles(): void {
    const previous = new Map<number, MediaFile>();
    this.mediaFiles().forEach(m => {
      if (m.data?.FileID > 0) { previous.set(m.data.FileID, m); }
    });

    const mf: MediaFile[] = this.files.map((f, index) => {
      const ext = this.getExtension(f.Name);
      const fileType = this.classifyFile(ext);
      const carried = f.FileID > 0 ? previous.get(f.FileID) : undefined;

      let thumbUrl: SafeUrl | string = carried?.thumbUrl ?? '';
      let previewUrl: SafeUrl | SafeResourceUrl | string = carried?.previewUrl ?? '';
      let previewState: PreviewState = carried?.previewState ?? 'none';
      const textContent = carried?.textContent ?? '';

      if (!carried && !(f.FileID > 0) && f.File && fileType === 'image') {
        const objUrl = this.trackObjectUrl(this.urlKey(f, index), URL.createObjectURL(f.File));
        thumbUrl = this.sanitizer.bypassSecurityTrustUrl(objUrl);
        previewUrl = this.sanitizer.bypassSecurityTrustUrl(objUrl);
        previewState = 'ready';
      }

      return {
        data: f,
        thumbUrl,
        previewUrl,
        previewState,
        textContent,
        fileType,
        extension: ext.toUpperCase(),
        friendlySize: this.getFriendlySize(f.File?.size || f.Size || 0),
      };
    });

    this.revokeObjectUrlsExcept(new Set(this.files.map((f, index) => this.urlKey(f, index))));

    this.mediaFiles.set(mf);
    if (mf.length > 0 && this.selectedIndex() >= mf.length) {
      this.selectedIndex.set(0);
    }
  }

  /** Replaces one entry, so an OnPush signal read actually sees the change. */
  private patchFile(p_fileId: number, p_patch: Partial<MediaFile>): void {
    this.mediaFiles.update(list =>
      list.map(m => (m.data?.FileID === p_fileId ? { ...m, ...p_patch } : m)));
  }

  /**
   * Same, keyed on the FileDataModel the entry wraps rather than on FileID. A file the user has
   * just added has no FileID yet (and several of them share 0), but buildMediaFiles maps the
   * same model objects on every rebuild, so the model is the stable identity.
   */
  private patchEntry(p_model: FileDataModel, p_patch: Partial<MediaFile>): void {
    this.mediaFiles.update(list =>
      list.map(m => (m.data === p_model ? { ...m, ...p_patch } : m)));
  }

  // ── Sidebar tiles: the server's PNG thumbnail ──
  //
  // GetPreview with a size resizes through Magick and writes PNG for EVERY image MIME it is
  // given, TIFF included - so one code path draws every tile and TIFF is not special. The
  // response is a base64 PNG, used as a data: URL: nothing to revoke, and the GET carries an
  // ETag so a reopened record redraws from cache.
  private async loadThumbnails(): Promise<void> {
    for (const f of this.files) {
      if (!(f.FileID > 0)) { continue; }
      if (this.classifyFile(this.getExtension(f.Name)) !== 'image') { continue; }

      const already = this.mediaFiles().find(m => m.data?.FileID === f.FileID);
      if (already?.thumbUrl) { continue; }

      try {
        const model = new RequestModel();
        model.url = 'api/media/GetPreview';
        model.data = { Id: f.FileID, Width: THUMB_EDGE, Height: THUMB_EDGE };

        const result = await firstValueFrom(this.restProvider.imagePostRequest(model));

        // ThumbnailService answers an empty body for anything it will not resize. An empty
        // data: URL is a broken <img>, so no thumbnail is left as no thumbnail and the tile
        // falls back to the named placeholder.
        if (result?.Base64Img) {
          this.patchFile(f.FileID, {
            thumbUrl: this.sanitizer.bypassSecurityTrustUrl('data:image/png;base64,' + result.Base64Img),
          });
        }
      } catch {
        // A tile that cannot be drawn is a placeholder, not a broken image.
      }
    }
  }

  // ── Centre panel: the full-size original, decoded if the browser cannot ──
  //
  // Fetched for the SELECTED file only, and once. The whole record used to be downloaded at
  // full size on open whether or not anything was looked at.
  //
  // Not only images: GetPreview at 0x0 answers a non-image file's original bytes verbatim
  // (StorageService.GetPreview), which is how the explorer this one replaced showed PDFs and
  // text files inside the app. Without this branch every PDF in "Naložene datoteke" was a badge
  // and a download button, i.e. the file could not be read without leaving the browser.
  private async ensurePreview(p_index: number): Promise<void> {
    const file = this.mediaFiles()[p_index];
    if (!file || !this.isInlineType(file.fileType)) { return; }
    if (file.previewState === 'ready' || file.previewState === 'loading') { return; }

    const model = file.data;
    const fileId = model?.FileID;
    // A saved file is fetched; a file just picked locally already has its bytes. Neither means
    // there is nothing to draw from, and the panel must not sit on a spinner saying so.
    if (!(fileId > 0) && !model?.File) {
      this.patchEntry(model, { previewState: 'unsupported' });
      return;
    }

    this.patchEntry(model, { previewState: 'loading' });

    let bytes: Uint8Array;
    try {
      if (fileId > 0) {
        const request = new RequestModel();
        request.url = 'api/media/GetPreview';
        request.data = { Id: fileId, Width: 0, Height: 0 };

        const result = await firstValueFrom(this.restProvider.imagePostRequest(request));
        if (!result?.Base64Img) {
          this.patchEntry(model, { previewState: 'unsupported' });
          return;
        }
        bytes = this.base64ToBytes(result.Base64Img);
      } else {
        bytes = new Uint8Array(await model.File.arrayBuffer());
      }
    } catch {
      this.patchEntry(model, { previewState: 'unsupported' });
      return;
    }

    const urlKey = this.urlKey(model, p_index);
    const mime = this.mimeOf(file);

    if (fileId > 0) {
      // The record page reads FileDataModel.File for its own gallery, so keep populating it.
      model.File = new File([bytes as BlobPart], model.Name, { type: mime });
      model.Size = bytes.byteLength;
    }

    if (file.fileType === 'text') {
      this.patchEntry(model, {
        textContent: new TextDecoder('utf-8').decode(bytes),
        previewState: 'ready',
        friendlySize: this.getFriendlySize(bytes.byteLength),
      });
      return;
    }

    if (file.fileType === 'pdf') {
      // The browser's own PDF viewer, in an iframe. It needs a RESOURCE url, and the blob must
      // be typed application/pdf or the frame offers a download instead of rendering.
      const url = this.trackObjectUrl(urlKey, URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime })));
      this.patchEntry(model, {
        previewUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url),
        previewState: 'ready',
        friendlySize: this.getFriendlySize(bytes.byteLength),
      });
      return;
    }

    if (file.fileType === 'audio' || file.fileType === 'video') {
      const url = this.trackObjectUrl(urlKey, URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime })));
      this.patchEntry(model, {
        previewUrl: this.sanitizer.bypassSecurityTrustUrl(url),
        previewState: 'ready',
        friendlySize: this.getFriendlySize(bytes.byteLength),
      });
      return;
    }

    if (TIFF_EXTS.includes(file.extension.toLowerCase())) {
      const decoded = await this.tiffPreview.toPng(
        bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer);

      if (decoded.ok !== true) {
        // The size still comes from the bytes that were actually fetched. A row whose Size
        // column was never populated would otherwise read "437 B" next to "too large to
        // display", which is the panel arguing with itself.
        this.patchEntry(model, {
          previewState: decoded.reason,
          friendlySize: this.getFriendlySize(bytes.byteLength),
        });
        return;
      }

      const url = this.trackObjectUrl(urlKey, URL.createObjectURL(decoded.blob));
      this.patchEntry(model, {
        previewUrl: this.sanitizer.bypassSecurityTrustUrl(url),
        previewState: 'ready',
        friendlySize: this.getFriendlySize(bytes.byteLength),
      });
      return;
    }

    // Everything the browser decodes natively goes straight in as its own bytes. Routing a
    // JPEG through a canvas would re-encode it for no reason and lose its colour profile.
    const url = this.trackObjectUrl(urlKey, URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime })));
    this.patchEntry(model, {
      previewUrl: this.sanitizer.bypassSecurityTrustUrl(url),
      previewState: 'ready',
      friendlySize: this.getFriendlySize(bytes.byteLength),
    });
  }

  private base64ToBytes(p_base64: string): Uint8Array {
    const bstr = atob(p_base64);
    const out = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) { out[i] = bstr.charCodeAt(i); }
    return out;
  }

  /** Whether the centre panel can draw this type from the file's own bytes. */
  isInlineType(p_type: FileType): boolean {
    return INLINE_TYPES.includes(p_type);
  }

  /** The stored MimeType when the row has one, otherwise what the extension says it is. */
  private mimeOf(p_file: MediaFile): string {
    const stored = (p_file.data?.MimeType || '').trim();
    if (stored && stored !== 'application/octet-stream') { return stored; }
    return MIME_BY_EXT[p_file.extension.toLowerCase()] || 'application/octet-stream';
  }

  /** What the centre panel says when it cannot draw the file. */
  previewMessage(p_file: MediaFile): string {
    if (p_file.previewState === 'too-large') {
      return this.translationsService.translate('FileExplorer_PreviewTooLarge');
    }
    if (TIFF_EXTS.includes(p_file.extension.toLowerCase())) {
      return this.translationsService.translate('FileExplorer_TiffPreviewUnavailable');
    }
    return this.translationsService.translate('FileExplorer_PreviewUnavailable');
  }

  // ── File classification ──
  classifyFile(ext: string): MediaFile['fileType'] {
    const e = ext.toLowerCase();
    if (IMAGE_EXTS.includes(e)) return 'image';
    if (PDF_EXTS.includes(e)) return 'pdf';
    if (DOC_EXTS.includes(e)) return 'document';
    if (TEXT_EXTS.includes(e)) return 'text';
    if (AUDIO_EXTS.includes(e)) return 'audio';
    if (VIDEO_EXTS.includes(e)) return 'video';
    return 'other';
  }

  getExtension(name: string): string {
    const parts = (name || '').split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  getFriendlySize(bytes: number): string {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // ── Selection ──
  selectFile(index: number): void {
    if (index >= 0 && index < this.mediaFiles().length) {
      this.selectedIndex.set(index);
      this.rotation.set(0);
      this.flippedH.set(false);
      this.exportError.set(null);
      this.loadProperties();
      this.ensurePreview(index);
    }
  }

  // ── View toggles ──
  setViewMode(mode: ViewMode): void { this.viewMode.set(mode); }
  setTab(tab: InfoTab): void { this.activeTab.set(tab); }

  // ── Zoom / Transform ──
  setZoom(mode: ZoomMode): void {
    this.zoomMode.set(mode);
    this.zoomLevel.set(mode === 'actual' ? 100 : mode === 'fill' ? 100 : 100);
  }
  zoomIn(): void { this.zoomLevel.update(z => Math.min(z + 25, 400)); }
  zoomOut(): void { this.zoomLevel.update(z => Math.max(z - 25, 25)); }
  rotateLeft(): void { this.rotation.update(r => r - 90); }
  rotateRight(): void { this.rotation.update(r => r + 90); }
  flipHorizontal(): void { this.flippedH.update(f => !f); }

  // ── Annotations (Annotorious) ──
  onImageLoad(): void {
    // Small delay to ensure DOM is stable after Angular renders
    setTimeout(() => this.initAnnotorious(), 100);
  }

  private initAnnotorious(): void {
    if (this.type === 2 || this.type === 3) return;

    // Destroy previous instance
    if (this.imageAnnotate) {
      try { this.imageAnnotate.destroy(); } catch {}
      this.imageAnnotate = null;
    }

    // Remove leftover annotation layers
    try {
      document.querySelectorAll('.a9s-annotationlayer').forEach(el => el.remove());
    } catch {}

    const imgEl = document.getElementById('me2-annotate');
    if (!imgEl) return;

    this.imageAnnotate = new Annotorious({
      image: imgEl,
      widgets: ['COMMENT'],
    });

    this.imageAnnotate.on('createAnnotation', (annotation: any) => {
      this.annotations.update(arr => [...arr, annotation]);
      const file = this.selectedFile();
      if (file) (file.data as any).Annotations = this.annotations();
    });

    this.imageAnnotate.on('updateAnnotation', (annotation: any) => {
      this.annotations.update(arr =>
        arr.map(a => a.id === annotation.id ? annotation : a)
      );
      const file = this.selectedFile();
      if (file) (file.data as any).Annotations = this.annotations();
    });

    this.imageAnnotate.on('deleteAnnotation', (annotation: any) => {
      this.annotations.update(arr => arr.filter(a => a.id !== annotation.id));
      const file = this.selectedFile();
      if (file) (file.data as any).Annotations = this.annotations();
    });

    this.loadAnnotationsForCurrentFile();
    this.imageAnnotate.setDrawingEnabled(this.annotationMode());
  }

  private loadAnnotationsForCurrentFile(): void {
    const file = this.selectedFile();
    if (!file || !this.imageAnnotate) {
      this.annotations.set([]);
      return;
    }

    const rawAnnotations = file.data.Annotations;
    const w3cAnnotations: any[] = [];
    let skipDraw = false;

    if (rawAnnotations && Array.isArray(rawAnnotations)) {
      for (const element of rawAnnotations) {
        let selector = null;
        if (element.target?.selector) {
          element.target.selector.type = 'FragmentSelector';
          selector = element.target.selector;
        }
        if (!selector) skipDraw = true;

        w3cAnnotations.push({
          '@context': 'http://www.w3.org/ns/anno.jsonld',
          id: element.id,
          type: 'Annotation',
          body: element.body,
          target: { selector },
        });
      }
    }

    this.annotations.set(w3cAnnotations);
    this.imageAnnotate.clearAnnotations();
    if (!skipDraw) {
      this.imageAnnotate.setAnnotations(w3cAnnotations);
    }
  }

  getAnnotationBody(ann: any): string {
    let str = '';
    ann.body?.forEach((element: any) => {
      str += ' ' + element.value + ';';
    });
    return str.length > 1 ? str.slice(0, -1).trim() : '';
  }

  toggleAnnotations(): void {
    this.annotationsVisible.update(v => !v);
    if (this.imageAnnotate) {
      this.imageAnnotate.setVisible(this.annotationsVisible());
    }
  }

  toggleAnnotationMode(): void {
    this.annotationMode.update(m => !m);
    if (this.imageAnnotate) {
      this.imageAnnotate.setDrawingEnabled(this.annotationMode());
    }
  }

  // ── Fullscreen ──
  toggleFullscreen(): void { this.isFullscreen.update(f => !f); }

  // ── Format conversion (TIFF export), performed from inside the Lastnosti tab ──
  async exportTiff(): Promise<void> {
    const file = this.selectedFile();
    if (!file || !this.canExport() || this.exportInProgress()) return;

    const fileId = file.data.FileID;
    this.exportInProgress.set(true);
    this.exportError.set(null);
    this.activeTab.set('properties');

    try {
      const res = await firstValueFrom(this.restProvider.convertFile(fileId, 'tiff'));

      // The export now WRITES to the record: the server appends the TIFF as an additional
      // FileData row on the same document and names it in this header. Show it in the file
      // list straight away, so a curator does not have to reopen the record to see what the
      // action they just took produced.
      const derivativeId = Number(res.headers.get('X-Derivative-File-Id') || 0);
      if (derivativeId > 0) {
        await this.appendDerivative(derivativeId);
      }

      const blob = res.body;
      if (blob) {
        const name = this.filenameFromDisposition(res.headers.get('Content-Disposition'))
          || this.stripExtension(file.data.Name) + '.tif';
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (e: any) {
      this.exportError.set(await this.readErrorMessage(e));
    } finally {
      this.exportInProgress.set(false);
      // A refused conversion is audited too, so refresh either way. The cached copy is dropped
      // first: the entry this call just wrote is in the history below the button, and a cache
      // hit would show the file's history as it was before the user pressed it.
      this.invalidateProperties(fileId);
      this.loadProperties(fileId);
    }
  }

  /**
   * Adds the preservation copy the server just wrote to the record's file list.
   *
   * The row's own facts come from GET api/media/properties/{id} rather than from a reload of
   * the record: that endpoint already exists, already answers for exactly one file and cannot
   * 401, so nothing else has to be refetched. The array is the one the host passed in, so the
   * component behind the dialog sees the same new entry.
   *
   * A failure here is not an export failure. The TIFF is on the record either way; the list is
   * one reopen behind, which is the documented fallback.
   */
  private async appendDerivative(derivativeId: number): Promise<void> {
    try {
      if (this.files.some(f => f.FileID === derivativeId)) { return; }

      const props = await firstValueFrom(this.restProvider.getFileProperties(derivativeId));
      const sys = props?.System;
      if (!props?.Available || !sys) { return; }

      const model = new FileDataModel({
        FileID: sys.FileId,
        Name: sys.Name,
        MimeType: sys.MimeType,
        Category: 1,
        FileDataBackendInfo: sys.StoragePath,
        Title: sys.Name,
        FileCategory: sys.FileCategory,
        FileOrder: sys.FileOrder,
        UploadDate: sys.UploadDate,
        LastModificationDate: sys.LastModificationDate,
        UniquePermissions: sys.UniquePermissions,
      });
      model.Size = sys.SizeBytes;
      // Old = a row that already exists on the server, which this one does.
      model.Action = ClientAction.Old;

      this.files.push(model);
      this.buildMediaFiles();
      this.filesChanged.emit(this.files);
      this.loadThumbnails();
    } catch {
      // See the doc comment: the file is on the record regardless.
    }
  }

  private filenameFromDisposition(disposition: string | null): string {
    if (!disposition) return '';
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8) {
      try { return decodeURIComponent(utf8[1].trim()); } catch { return utf8[1].trim(); }
    }
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    return plain ? plain[1].trim() : '';
  }

  private stripExtension(name: string): string {
    const i = (name || '').lastIndexOf('.');
    return i > 0 ? name.substring(0, i) : (name || 'export');
  }

  /**
   * What the user is told when a conversion fails.
   *
   * The status code used to arrive as undefined and the box read "HTTP ?", because the global
   * ServerErrorsInterceptor rethrew a formatted string rather than the response. Requests that
   * opt out of the global 401 handling now receive the HttpErrorResponse, so the blob body below
   * is actually reachable; the string branch stays as a fallback for any other rethrow shape.
   */
  private async readErrorMessage(e: any): Promise<string> {
    try {
      const status = this.statusOf(e);

      // A refusal is a permissions refusal, and saying so is the whole point.
      if (status === 401 || status === 403) {
        return this.translationsService.translate('FileExplorer_ExportNoRight');
      }

      // The API answers with a blob because the success path is a file download.
      if (e?.error instanceof Blob) {
        const text = (await e.error.text()).trim();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            const msg = parsed.Message || parsed.message || parsed.Error || parsed.error;
            if (msg) { return String(msg); }
          } catch { }
          // A short plain body is the server's own message. An HTML error page is not one.
          if (text.length <= 400 && !/^\s*</.test(text)) { return text; }
        }
      }

      if (typeof e?.error === 'string' && e.error) { return e.error; }
      if (typeof e?.error?.Message === 'string' && e.error.Message) { return e.error.Message; }

      // The interceptor's own formatted string is not a message for a user; statusOf has already
      // taken the only useful thing out of it.
      return status
        ? this.translationsService.translate('FileExplorer_ExportFailedStatus') + ' ' + status + '.'
        : this.translationsService.translate('FileExplorer_ExportNoResponse');
    } catch {
      return this.translationsService.translate('FileExplorer_ExportFailed');
    }
  }

  /** The HTTP status, whatever shape the failure arrived in. 0 when there is none. */
  private statusOf(e: any): number {
    if (typeof e?.status === 'number' && e.status > 0) { return e.status; }
    const text = typeof e === 'string' ? e : (typeof e?.message === 'string' ? e.message : '');
    const m = /Error Code:\s*(\d{3})/.exec(text);
    return m ? Number(m[1]) : 0;
  }

  dismissExportError(): void { this.exportError.set(null); }

  shortHash(hash: string | null): string {
    if (!hash) return '—';
    return hash.length > 12 ? hash.substring(0, 8) + '…' : hash;
  }

  // ── File properties (Lastnosti) ──
  loadProperties(fileId?: number): void {
    const id = fileId ?? this.selectedFile()?.data?.FileID ?? 0;
    if (!id || id <= 0) {
      this.properties.set(null);
      return;
    }

    const cached = this.propertiesCache.get(id);
    if (cached) {
      this.properties.set(cached);
      this.propertiesUnavailable.set(false);
      return;
    }

    this.propertiesLoading.set(true);
    this.restProvider.getFileProperties(id).subscribe({
      next: (data: FileProperties) => {
        this.propertiesCache.set(id, data);
        // The user may have moved to another file while this was in flight.
        if ((this.selectedFile()?.data?.FileID ?? 0) !== id) return;
        this.properties.set(data);
        this.propertiesUnavailable.set(false);
        this.propertiesLoading.set(false);
      },
      error: () => {
        if ((this.selectedFile()?.data?.FileID ?? 0) !== id) return;
        // The endpoint answers Available = false rather than refusing, so this is a transport
        // failure. The panel goes quiet and the export action goes disabled WITH its reason;
        // the gallery and every other tab keep working.
        this.properties.set(null);
        this.propertiesUnavailable.set(true);
        this.propertiesLoading.set(false);
      },
    });
  }

  /** Drop the cached copy of one file's properties, so the next read goes to the server. */
  private invalidateProperties(fileId: number): void {
    this.propertiesCache.delete(fileId);
  }

  /**
   * A date dbo.filedata never recorded. NHibernate materialises those as DateTime.MinValue, and
   * the rows imported before the column was populated all carry it - printing "1. 1. 1" would
   * state an upload date the system does not have.
   */
  hasDate(value: string | null | undefined): boolean {
    if (!value) return false;
    const t = Date.parse(value);
    return !isNaN(t) && new Date(t).getUTCFullYear() > 1;
  }

  /** The label a metadata group renders under. Unknown keys render as themselves. */
  metadataGroupLabel(key: string): string {
    const translated = this.translationsService.translate('FileExplorer_Metadata_' + key);
    return translated === 'FileExplorer_Metadata_' + key ? key : translated;
  }

  // ── File operations ──
  downloadFile(): void {
    const file = this.selectedFile();
    if (!file || this.downloadInProgress()) return;

    if (file.data.FileID > 0) {
      this.downloadInProgress.set(true);
      const model = new RequestModel();
      model.url = 'api/media/getfile';
      model.data = { Id: file.data.FileID, FileName: file.data.Name };

      this.restProvider.getDataPOST(model).subscribe({
        next: (data: any) => {
          const link = document.createElement('a');
          link.href = `data:${data['MimeType']};base64,${data['Base64']}`;
          link.download = file.data.Name;
          link.click();
          this.downloadInProgress.set(false);
        },
        error: () => this.downloadInProgress.set(false),
      });
    }
    this.onItemDownload.emit(file.data);
  }

  deleteFile(): void {
    const file = this.selectedFile();
    if (!file) return;
    this.onItemDelete.emit(file.data);
    // Mutate in-place so caller's reference sees the change
    const idx = this.selectedIndex();
    this.files.splice(idx, 1);
    this.buildMediaFiles();
    if (idx >= this.mediaFiles().length) {
      this.selectedIndex.set(Math.max(0, this.mediaFiles().length - 1));
    }
  }

  addFile(): void {
    this.onItemAdd.emit();
  }

  close(): void {
    this.dialogRef?.close();
  }

  // ── Filmstrip scroll ──
  scrollFilmstrip(dir: 'left' | 'right'): void {
    if (this.filmstripEl) {
      const el = this.filmstripEl.nativeElement;
      el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
    }
  }

  // ── Keyboard navigation ──
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.selectFile(Math.min(this.selectedIndex() + 1, this.mediaFiles().length - 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.selectFile(Math.max(this.selectedIndex() - 1, 0));
    } else if (e.key === 'Escape') {
      if (this.isFullscreen()) this.toggleFullscreen();
      else if (this.annotationMode()) this.toggleAnnotationMode();
    }
  }

  // ── Doc thumbnail colors ──
  getDocColor(ext: string): { bg: string; badge: string; text: string } {
    const e = ext.toLowerCase();
    if (PDF_EXTS.includes(e)) return { bg: '#fef2f2', badge: '#ef4444', text: 'PDF' };
    if (['doc', 'docx', 'odt'].includes(e)) return { bg: '#eff6ff', badge: '#3b82f6', text: 'DOC' };
    if (['xls', 'xlsx', 'ods'].includes(e)) return { bg: '#ecfdf5', badge: '#22c55e', text: 'XLS' };
    if (['ppt', 'pptx', 'odp'].includes(e)) return { bg: '#fff7ed', badge: '#f97316', text: 'PPT' };
    return { bg: '#f8fafc', badge: '#94a3b8', text: ext.toUpperCase() };
  }

  // ── Track by ──
  trackByIndex(index: number): number { return index; }
}
